// frontend/src/context/PaletteModeContext.tsx
// <purpose>Per-user light/dark palette preference (rejection + archive mode families). Active mode is unaffected.</purpose>
// <constraints>FE-only: persisted in localStorage 'stx-palette-mode'. Default 'light'. No backend.</constraints>
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type PaletteMode = 'light' | 'dark';
const KEY = 'stx-palette-mode';

interface Ctx { mode: PaletteMode; setMode: (m: PaletteMode) => void; toggle: () => void; }
const PaletteModeCtx = createContext<Ctx>({ mode: 'light', setMode: () => {}, toggle: () => {} });

function readStored(): PaletteMode {
  try { return localStorage.getItem(KEY) === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
}

export const PaletteModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<PaletteMode>(readStored);
  const setMode = useCallback((m: PaletteMode) => {
    setModeState(m);
    try { localStorage.setItem(KEY, m); } catch { /* private mode / quota — preference is best-effort */ }
  }, []);
  // Re-reads storage (not the captured `mode`) so the stable callback always flips the
  // current value — capturing `mode` would freeze it at first-render and always toggle from light.
  const toggle = useCallback(() => setMode(readStored() === 'dark' ? 'light' : 'dark'), [setMode]);
  // Memoized to match ClassificationSchemaContext — keeps consumers from re-rendering on every App render.
  const value = useMemo(() => ({ mode, setMode, toggle }), [mode, setMode, toggle]);
  return <PaletteModeCtx.Provider value={value}>{children}</PaletteModeCtx.Provider>;
};

export function usePaletteMode(): Ctx { return useContext(PaletteModeCtx); }
